import React from 'react';
import Input from 'src/shared/components/input/Input';
import InfoMessage from 'src/shared/components/info-message/InfoMessage';
import { UserType } from 'src/shared/types';
import EditProjectUserHeader from './EditProjectUserHeader';
import ChangeUserType from '../../change-user-type/ChangeUserType';
import type { EditProjectUserFormState } from '../../../state/userManagementSlice';
import styles from '../EditProjectUser.module.scss';

type EditProjectUserProfileFieldsStepProps = {
    formState: EditProjectUserFormState['formState'];
    updateEditUserForm: (fragment: Partial<EditProjectUserFormState['formState']>) => void;
    isCountryCodeValid: boolean;
    onPhoneCountryBlur: () => void;
    notifyCheckbox: React.ReactNode;
    backButton: React.ReactNode;
    profileSaveButton: React.ReactNode;
    setErrorMessage: (message: string) => void;
};

/** `editUserView === 'edit-user-details'`: editable name/phone plus read-only email */
const EditProjectUserProfileFieldsStep = ({
    formState,
    updateEditUserForm,
    isCountryCodeValid,
    onPhoneCountryBlur,
    notifyCheckbox,
    backButton,
    profileSaveButton,
    setErrorMessage,
}: EditProjectUserProfileFieldsStepProps) => (
    <>
        <div className={styles.section}>
            <EditProjectUserHeader setErrorMessage={setErrorMessage} hideEditLink={true} />
        </div>
        <span className={styles.sectionDivider}></span>
        <div className={styles.fields} data-testid='user-management-edit-user-profile-fields'>
            <Input
                value={formState.firstName}
                placeholder='First name'
                autoComplete='off'
                onChange={event => updateEditUserForm({ firstName: event.target.value })}
                data-testid='user-management-edit-user-first-name'
            ></Input>
            <Input
                value={formState.lastName}
                placeholder='Last name'
                autoComplete='off'
                onChange={event => updateEditUserForm({ lastName: event.target.value })}
                data-testid='user-management-edit-user-last-name'
            ></Input>
            <div className={styles.phoneInput}>
                <Input
                    value={formState.phoneCountryCode ?? ''}
                    placeholder='+44'
                    hasError={!isCountryCodeValid}
                    maxLength={4}
                    hideMaxLengthLabel={true}
                    onBlur={onPhoneCountryBlur}
                    onChange={event => updateEditUserForm({ phoneCountryCode: event.target.value })}
                    data-testid='user-management-edit-user-phone-country-code'
                ></Input>
                <Input
                    /* National number only in UI; parent still stores full number in `formState.phone` */
                    value={formState.phone ? formState.phone.replace(formState.phoneCountryCode ?? '', '') : ''}
                    placeholder='Mobile number'
                    onChange={event => updateEditUserForm({ phone: event.target.value })}
                    autoComplete='off'
                    maxLength={15}
                    hasError={Boolean(formState.phone) && !isFinite(Number(formState.phone))}
                    hideMaxLengthLabel={true}
                    onBlur={onPhoneCountryBlur}
                    data-testid='user-management-edit-user-phone'
                ></Input>
            </div>
            <Input value={formState.email} placeholder='Email' disabled data-testid='user-management-edit-user-email-readonly'></Input>
        </div>
        <InfoMessage
            message={
                <>
                    User account email address cannot be changed. <br />
                    <br /> If this email address is no longer valid, please deactivate this user and create a new user account
                </>
            }
        />
        <div className={`${styles.section} ${styles.userTypeSection}`} data-testid='user-management-edit-user-type'>
            <ChangeUserType
                email={formState.email}
                userType={formState.is_internal_user ? UserType.INTERNAL : UserType.EXTERNAL}
                onChanged={changedTo => updateEditUserForm({ is_internal_user: changedTo === UserType.INTERNAL })}
            />
        </div>
        <div>{notifyCheckbox}</div>

        <div className={`${styles.bottomNavigation} ${styles['bottomNavigationRow']}`}>
            {backButton}
            {profileSaveButton}
        </div>
    </>
);

export default EditProjectUserProfileFieldsStep;
